import { RoutesService } from '@rightship-tech/common-ui/services/routes/routes.service';
import { RouteGenerator } from '@rightship-tech/common-ui/route-generator';
import { ConfigService } from '@rightship-tech/common-ui/services/basic/config.service';
import { DataDogService } from '@rightship-tech/common-ui/services/analytics/data-dog.service';
import { UserClaimsService } from '@rightship-tech/common-ui/services/basic/user-claims.service';
import { UserClientService } from '@rightship-tech/common-ui/services/basic/user-client.service';
import { Aurelia, DOM, PLATFORM } from 'aurelia-framework';
import * as moment from "moment";
import { SignalClientService } from '@rightship-tech/common-ui/services/basic/signal-client.service';
import { CookieHelper } from '@rightship-tech/common-ui/helpers/cookie-helper';
window["moment"] = moment; //Done in order for boostrap-datetimepicker.js finds moment globally

//Load styles
import './styles/core.scss';
import { EncodingHelper } from '@rightship-tech/common-ui/helpers/encoding.helper';

// /**
//  * Stores the query string to a cookie so that it can be used after login, in the dashboard page
//  * Redirects to signup page with root as returnUrl, does not need to pass the query string as it is stored in a cookie
//  */
export function redirectToSignUp(configService: ConfigService) {

    let campaign: string = "rightfleet-new-rfi";

    var queryString = window.location.search;
    CookieHelper.createCookie(`queryString-${configService.config.environment}`, queryString, 1, configService.config.environment);

    //Environmnetal reviews query string - ET1-334
    let environmentalReviews;
    const urlParams = new URLSearchParams(queryString);

    if (urlParams.get("env-reviews") && urlParams.get("env-reviews") != "") {
        environmentalReviews = EncodingHelper.decodeUrl(urlParams.get("env-reviews")) as unknown as boolean;

        //Set campaign if environmental reviews is passed as true
        if (environmentalReviews) {
            campaign = "proactive-ghg-verification";
        }
    }

    //Redirect
    window.location.href = configService.config.urls.rightShipSignupUrl + "?utm_campaign="+ campaign +"&returnUrl=" + window.location.origin + window.location.pathname;
}

export async function configure(aurelia: Aurelia): Promise<void> {

    //Get config.json file
    let configService = await aurelia.container.get(ConfigService) as ConfigService;

    //DataDog - Starting the DataDog RUM
    aurelia.container.get(DataDogService).init();

    //Google tag manager - Injecting the script into the head of the DOM so that we can pass the correct key based on the environment
    if(configService.config.analytics?.googleAnalytics?.tagManagerKey) {
        const script = DOM.createElement('script');
        script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${configService.config.analytics.googleAnalytics.tagManagerKey}');`;
        DOM.appendNode(script, document.head);
    }

    //Intercom - Injecting the script into the head of the DOM so that we can pass the correct key based on the environment
    if(configService.config.settings.intercomAppId) {
        const intercomScript = DOM.createElement('script');
        intercomScript.innerHTML = `(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}
            else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');
            s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/${configService.config.settings.intercomAppId}';
            var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(w.attachEvent){w.attachEvent('onload',l);}
            else{w.addEventListener('load',l,false);}}})();`;
        DOM.appendNode(intercomScript, document.head);
    }

    //Log current environment and version number
    console.log(`running on ${configService.config.environment} with version number ${configService.config.version}`)

    //Config
    aurelia.use.standardConfiguration();
    aurelia.use.developmentLogging();

    //Aurelia plugins
    aurelia.use.plugin(PLATFORM.moduleName('aurelia-validation'));
    aurelia.use.plugin(PLATFORM.moduleName('aurelia-ui-virtualization'));

    //Plugins
    aurelia.use.plugin(PLATFORM.moduleName("@rightship-tech/common-ui"));
    aurelia.use.plugin(PLATFORM.moduleName("@rightship-tech/pscs-ui"));
    aurelia.use.plugin(PLATFORM.moduleName("@rightship-tech/tasks-ui"));
    aurelia.use.plugin(PLATFORM.moduleName("@rightship-tech/document-management-app"));

    //Load global resources
    aurelia.use.globalResources([

        //Components
        PLATFORM.moduleName("views/components/comp-task-card/comp-task-card"),

        //Partials
        PLATFORM.moduleName("views/partials/pending-tasks/partial-pending-tasks"),

        //Pages
        PLATFORM.moduleName("views/pages/dashboard/dashboard"),
        PLATFORM.moduleName("views/pages/tasks/all-tasks/all-tasks"),
        PLATFORM.moduleName("views/pages/rfi-correspondence/rfi-correspondence"),
        PLATFORM.moduleName("views/pages/vessel-documents/vessel-documents"),
        PLATFORM.moduleName("views/pages/common/submit-close-out-information/submit-close-out-information"),
        PLATFORM.moduleName("views/pages/common/select-vessel/select-vessel"),
        PLATFORM.moduleName("views/pages/not-found/not-found")
    ]);

    try {

        //Generate routes
        const routeGenerator: RouteGenerator = aurelia.container.get(RouteGenerator);
        const routes = await aurelia.container.get(RoutesService).getRoutes();

        //Set routes
        for (var route of routes) {
            routeGenerator.addRoute(route);
        }

        //Start Aurelia
        await aurelia.start();

        //Get current user
        let currentUser = await aurelia.container.get(UserClientService).getCurrentUser();

        //Redirect to signup if user is not logged in
        if (!currentUser) {
            redirectToSignUp(configService);
        }
        else {

            //Set user claims
            let me = await aurelia.container.get(UserClientService).getMe();
            aurelia.container.get(UserClaimsService).setUserClaims(me.allowedClaims);

            //Load XSRF token and start signalR listener
            await aurelia.container.get(UserClientService).loadXSRFToken();
            await aurelia.container.get(SignalClientService).startListener();

            //Set layout page as root page
            await aurelia.setRoot(PLATFORM.moduleName("views/pages/layout/layout"));
        }
    } catch (error) {
        console.log(error);
        redirectToSignUp(configService);
    }
}